import { ReactNode, useMemo } from 'react';
import {
  createTheme,
  CssBaseline,
  Paper,
  ThemeProvider as MaterialThemeProvider,
} from '@mui/material';

/**
 * Universal+ theming for the operator tool.
 *
 * The tokens are sampled from the live universalplus.com, not invented: the site is near-black with
 * a single gold accent on calls to action, dark ink for text on that gold, and Open Sans throughout.
 * The purple is theirs too, used sparingly.
 *
 * One conflict had to be resolved deliberately. The brand accent is a **yellow-gold**, and MUI's
 * default `warning` is an amber close enough to be mistaken for it. That matters here more than it
 * would in most apps, because this tool renders validation warnings in the warning colour and the
 * whole point is that an operator can tell a warning from an ordinary control at a glance. So
 * `warning` is pushed to a distinctly orange tone that cannot be read as "brand accent", and the
 * gold is reserved for actions.
 *
 * Note the app deliberately does **not** use the Universal+ logo. This is a Merapar-built internal
 * tool, not a Universal product, and shipping their mark in it is a trademark decision for the
 * customer to make rather than an implementation detail.
 *
 * The component overrides below are lifted from CVDS, which is the point: they make a validation
 * warning render as amber rather than red on an otherwise-error-styled field, which is the visual
 * language operators there already know.
 */
const BRAND = {
  /** The call-to-action gold, from the site's primary button. */
  primary: '#fccc12',
  primaryDark: '#d4a900',
  /** Text on the gold — their near-black ink, not pure black. */
  onPrimary: '#030c17',
  /**
   * Universal Corporate Blue, per the published brand assets.
   *
   * This token held `#400094`, a deep purple, which was wrong twice over: it is not in the brand
   * palette, and at that darkness it is invisible against a near-black page — it marked unsaved edits
   * until that turned out to hide the signal entirely, and it was still colouring the Export button,
   * the most important action on the item page.
   */
  secondary: '#005395',
  /** A lighter mix of the same blue, because #005395 on near-black is too low-contrast for a fill. */
  secondaryLight: '#1b6fb5',
  /**
   * Yukon Gold, the darker half of the brand's gold pair. Used for the page's ambient gradient and for
   * the pressed state of a gold button — never for text, where it fails contrast on black.
   */
  goldDeep: '#7e6609',
  backgroundDefault: '#000000',
  backgroundPaper: '#0b1018',
  /** A lift above `paper`, so nested surfaces read as separate on an otherwise black page. */
  surface: '#151b25',
  textSecondary: '#bbbbbb',
  divider: 'rgba(255, 255, 255, 0.12)',
  /** Clearly orange, so a validation warning is never mistaken for the brand accent. */
  warning: '#ff8a3d',
} as const;

const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const theme = useMemo(() => {
    const defaultTheme = createTheme();

    return createTheme({
      palette: {
        mode: 'dark',
        primary: {
          main: BRAND.primary,
          dark: BRAND.primaryDark,
          // Gold is bright: white text on it is unreadable, so the brand's own ink is the pair.
          contrastText: BRAND.onPrimary,
        },
        secondary: { main: BRAND.secondaryLight, dark: BRAND.secondary },
        warning: { main: BRAND.warning },
        background: {
          default: BRAND.backgroundDefault,
          paper: BRAND.backgroundPaper,
        },
        text: { primary: '#ffffff', secondary: BRAND.textSecondary },
        divider: BRAND.divider,
      },
      // The site's own button radius.
      shape: { borderRadius: 8 },
      typography: {
        fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
        // The site sets its calls to action in bold; matching it is most of what makes the tool
        // read as the same product.
        button: { fontWeight: 700, textTransform: 'none' },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700, letterSpacing: '0.02em' },
        subtitle2: { fontWeight: 600 },
      },
      components: {
        /**
         * The ambient gradient from the site: a warm gold glow bleeding in from the right edge of an
         * otherwise black page.
         *
         * Deliberately far weaker than the marketing site's. This is a tool people spend a working day
         * in, not a landing page, so the gradient is there to stop the background reading as flat black
         * and nothing more — at these alphas it is barely nameable, which is the point. Fixed
         * attachment so it does not travel with a scrolling table and turn into motion.
         */
        MuiCssBaseline: {
          styleOverrides: {
            /**
             * The native date/time picker's calendar glyph is a dark, fixed-colour bitmap that ignores
             * the theme, so on a near-black field it was black on dark grey and effectively invisible —
             * an operator could not see there was a picker to open at all.
             *
             * Inverted to white, and given a hover state so it reads as interactive. Applies to
             * `date`, `datetime-local` and `time` inputs. Firefox draws its own indicator that follows
             * `color-scheme`, so this is Chromium/WebKit only by design rather than by omission.
             */
            'input[type="date"]::-webkit-calendar-picker-indicator, input[type="datetime-local"]::-webkit-calendar-picker-indicator, input[type="time"]::-webkit-calendar-picker-indicator':
              {
                // Hidden, not restyled. Inverting the native glyph made it visible but not legible, and
                // its shape barely reads as a calendar at this size — a `filter` fixes neither. A real MUI
                // icon button sits in the adornment slot instead (see DateCell), so it inherits the text
                // colour like every other control and is unmistakably a calendar. The input underneath is
                // still a native date input, which is what keeps the picker free of timezone handling.
                display: 'none',
              },
            // Without this the dropdown panel the picker opens is rendered light-on-light by the browser,
            // which is the same problem one layer further in.
            ':root': { colorScheme: 'dark' },
            body: {
              backgroundColor: BRAND.backgroundDefault,
              backgroundImage: `radial-gradient(115% 75% at 100% 45%, rgba(126, 102, 9, 0.20) 0%, rgba(126, 102, 9, 0.07) 38%, rgba(0, 0, 0, 0) 68%)`,
              backgroundAttachment: 'fixed',
              backgroundRepeat: 'no-repeat',
            },
          },
        },
        /**
         * Buttons, matched to the site's pair: a filled gold call to action and a gold-outlined
         * secondary next to it.
         *
         * MUI's defaults were the gap here — uppercase, medium weight and a drop shadow, none of which
         * the brand uses — so a gold button still did not read as one of theirs.
         */
        MuiButton: {
          defaultProps: {
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 700,
              // Their buttons are squarer than MUI's default rounding.
              borderRadius: 6,
            },
            containedPrimary: {
              backgroundColor: BRAND.primary,
              color: BRAND.onPrimary,
              '&:hover': { backgroundColor: BRAND.primaryDark },
              '&:active': { backgroundColor: BRAND.goldDeep },
            },
            outlinedPrimary: {
              // "Iniciar sesión": gold rule, light label, transparent fill.
              borderColor: BRAND.primary,
              color: '#ffffff',
              '&:hover': {
                borderColor: BRAND.primary,
                backgroundColor: 'rgba(252, 204, 18, 0.08)',
              },
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              // Flat black with a gold hairline, rather than the previous gradient. The site is
              // resolutely flat and lets the accent do the work; a gradient reads as a different
              // product.
              backgroundImage: 'none',
              backgroundColor: BRAND.backgroundDefault,
              borderBottom: `2px solid ${BRAND.primary}`,
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: BRAND.backgroundPaper,
              borderRight: `1px solid ${BRAND.divider}`,
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              // The selected nav item is marked with the accent as a rule rather than a fill, so a
              // whole gold row never competes with the primary action on the page.
              '&.Mui-selected': {
                backgroundColor: 'rgba(252, 204, 18, 0.10)',
                borderLeft: `3px solid ${BRAND.primary}`,
                '&:hover': { backgroundColor: 'rgba(252, 204, 18, 0.16)' },
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            // Surfaces need to separate from a pure-black page; the default dark elevation tints
            // barely register against #000.
            root: { backgroundImage: 'none' },
            elevation1: { backgroundColor: BRAND.surface },
          },
        },
        MuiTableHead: {
          styleOverrides: {
            root: { '& .MuiTableCell-head': { fontWeight: 700 } },
          },
        },
        MuiAutocomplete: {
          defaultProps: {
            // stronger shadow to distinguish from the background
            PaperComponent: ({ children }) => (
              <Paper elevation={4}>{children}</Paper>
            ),
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              // A field carrying only a WARNING is still `Mui-error` structurally, but must not read
              // as an error. Same trick as CVDS.
              '.MuiFormControl-root.warning .Mui-error &': {
                borderColor: defaultTheme.palette.warning.light,
              },
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              '.warning &.Mui-error, &.warning.Mui-error': {
                color: defaultTheme.palette.warning.main,
              },
            },
          },
        },
      },
    });
  }, []);

  return (
    <MaterialThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MaterialThemeProvider>
  );
};

export default ThemeProvider;
